import React from 'react'

function invokeCallback(callback, ...args) {
  if (typeof callback === 'function') {
    callback(...args)
  }
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function resolveDailyAudioEnabled(participant = {}, fallbackValue = false) {
  if (typeof participant?.audioEnabled === 'boolean') {
    return participant.audioEnabled
  }

  const normalizedMicState = normalizeOptionalString(participant?.micState, '')

  if (normalizedMicState === 'on' || normalizedMicState === 'playable') {
    return true
  }

  if (normalizedMicState === 'off' || normalizedMicState === 'blocked') {
    return false
  }

  return fallbackValue
}

function resolveDailyVideoEnabled(participant = {}, fallbackValue = false) {
  if (typeof participant?.videoEnabled === 'boolean') {
    return participant.videoEnabled
  }

  const normalizedCameraState = normalizeOptionalString(participant?.cameraState, '')

  if (['playable', 'loading', 'interrupted'].includes(normalizedCameraState)) {
    return true
  }

  if (['off', 'blocked'].includes(normalizedCameraState)) {
    return false
  }

  return fallbackValue
}

function buildFixtureParticipants(rtcProps = {}) {
  if (Array.isArray(rtcProps?.fixtureParticipants) && rtcProps.fixtureParticipants.length > 0) {
    return rtcProps.fixtureParticipants
  }

  if (!rtcProps?.participantMetadata && !rtcProps?.userName) {
    return []
  }

  return [
    {
      sessionId: 'daily-local-fixture',
      isLocal: true,
      userName: rtcProps.userName || 'Local participant',
      metadata: rtcProps.participantMetadata || null,
      audioEnabled: rtcProps.enableAudio !== false,
      videoEnabled: rtcProps.enableVideo !== false,
      screenSharing: rtcProps.enableScreensharing === true,
    },
  ]
}

export function DailyRtcCompatibilityShell({
  rtcProps = {},
  callbacks = {},
  fallback = null,
  onProviderError,
  renderless = false,
  children = null,
}) {
  const fixtureParticipants = React.useMemo(
    () => buildFixtureParticipants(rtcProps),
    [
      rtcProps?.enableAudio,
      rtcProps?.enableScreensharing,
      rtcProps?.enableVideo,
      rtcProps?.fixtureParticipants,
      rtcProps?.participantMetadata,
      rtcProps?.userName,
    ],
  )

  React.useEffect(() => {
    if (!rtcProps?.roomUrl) {
      invokeCallback(onProviderError, new Error('Missing Daily.co room url'))
      return
    }

    invokeCallback(
      callbacks['connection-state-change'],
      rtcProps?.initialSessionState || 'joined',
      'idle',
      null,
    )
  }, [callbacks, onProviderError, rtcProps?.initialSessionState, rtcProps?.roomUrl])

  React.useEffect(() => {
    invokeCallback(callbacks['participants-updated'], {
      participants: fixtureParticipants,
    })
  }, [callbacks, fixtureParticipants])

  React.useEffect(() => {
    const localParticipant =
      fixtureParticipants.find((participant) => participant?.isLocal === true) || null

    if (!localParticipant) {
      return
    }

    invokeCallback(
      callbacks.onAudioStateChange,
      resolveDailyAudioEnabled(localParticipant, rtcProps?.enableAudio !== false),
    )
    invokeCallback(
      callbacks.onCameraStateChange,
      resolveDailyVideoEnabled(localParticipant, rtcProps?.enableVideo !== false),
    )
  }, [callbacks, fixtureParticipants, rtcProps?.enableAudio, rtcProps?.enableVideo])

  if (renderless) {
    return children
  }

  if (fallback) {
    return fallback
  }

  return (
    <div
      data-daily-rtc-compatibility-shell="true"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
        color: '#e2e8f0',
        fontSize: '14px',
        fontWeight: 600,
      }}
    >
      Daily.co scaffold active
    </div>
  )
}

export default DailyRtcCompatibilityShell
